import React, { useEffect, useState, useCallback } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Typography, IconButton, Tooltip } from '@mui/material';
import { isEnabledModule } from '#component/project/form/helper';
import DisabledModuleMessage from '#component/project/form/project/project-modules/disabled-module-message';
import {
  AddButtonIcon,
  ButtonContainer,
  HintContainer,
  HintIcon,
  HintText,
  StyledButton,
  StyledPaper,
  StyledTitle
} from './styles';
import AlertItem from './alert-item';
import { useIntersection } from '#hooks';
import { uuidv4 } from '#helper';

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

const DEFAULT_ALERT_START_DELAY = 30; // In minutes
const DEFAULT_DELAY_BETWEEN_ALERTS = 1; // In hour

/**
 * Renders form with submit, go back
 * and add alert buttons.
 * @returns {React.FC}
 */

const AlertsForm = props => {
  const {
    submitting,
    previousStep,
    nextStep,
    history,
    forceError,
    formModules,
    editProject,
    submitForm,
    saveDisabled,
    setForceError,
    markAlertErrors
  } = props;
  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);
  const { control, watch, trigger, getFieldState, formState: { errors } } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alerts',
    keyName: 'fieldId'
  });
  const items = watch('alerts') ?? [];
  const arrayError = errors.alerts?.root?.message;

  const shipmentModuleEnabled = isEnabledModule('SHIPMENT', formModules);
  const siteModuleEnabled = isEnabledModule('SITE', formModules);
  const isFormEnabled = shipmentModuleEnabled || siteModuleEnabled;

  const [expanded, setExpanded] = useState(
    fields.length ? `alerts[${fields.length - 1}]` : false
  );

  const handleSetExpanded = useCallback(panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  useEffect(() => {
    if (fields.length) {
      setExpanded(`alerts[${fields.length - 1}]`);
    }
  }, [fields.length]);

  const handleCancelClick = () => {
    history.push('/project');
  };

  // Only the alerts are validated, so errors on other steps do not block adding one.
  // Array form, as a single name only re-renders a field registered with that exact name.
  const addAlertItem = async () => {
    if (await trigger(['alerts'])) {
      append({
        id: uuidv4(),
        startDelay: { minute: DEFAULT_ALERT_START_DELAY },
        minAlertPeriod: { hour: DEFAULT_DELAY_BETWEEN_ALERTS }
      });
    } else {
      setForceError(true);
      markAlertErrors(getFieldState('alerts').error);
    }
  };

  return (
    <form onSubmit={submitForm} autoComplete="off">
      {isFormEnabled ?
        <>
          <StyledTitle variant="h4" gutterBottom>
            Alert settings
          </StyledTitle>
          <StyledPaper elevation={0}>
            {editProject && (
              <HintContainer>
                <HintIcon color="disabled" />
                <HintText variant="body2" gutterBottom color="gray">
                  Existing shipments and applications are not affected by changes made here.
                </HintText>
              </HintContainer>
            )}
            {fields.map((field, index) => (
              <AlertItem
                key={field.fieldId}
                item={items[index] ?? field}
                index={index}
                member={`alerts[${index}]`}
                remove={remove}
                numberOfItems={fields.length}
                expanded={expanded === `alerts[${index}]`}
                handleSetExpanded={handleSetExpanded}
                forceError={forceError}
              />
            ))}
            {arrayError &&
              <Typography align="center" color="error">{arrayError}</Typography>}
          </StyledPaper>
        </> : <DisabledModuleMessage />}
      <div ref={sentinelRef} />
      <ButtonContainer
        isFormEnabled={isFormEnabled}
        isIntersecting={isIntersecting}
      >
        {isFormEnabled ?
          <div>
            <IconButton size="small" onClick={addAlertItem} aria-label="Add alert setting">
              <Tooltip title="Add alert setting">
                <AddButtonIcon />
              </Tooltip>
            </IconButton>
          </div> : null}
        <div>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={previousStep}
          >
            Back
          </StyledButton>
          <StyledButton
            disabled={submitting}
            onClick={nextStep}
            variant="outlined"
          >
            Next
          </StyledButton>
          <StyledButton
            disabled={saveDisabled}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </div>
      </ButtonContainer>
    </form>
  );
};

export default AlertsForm;
